import React, { useState, useEffect, useCallback, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { SwiperClass } from 'swiper/react'
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons'

import classNames from 'classnames'
import classes from './styles.module.scss'

interface NavigationProps {
  hasNextPage: boolean
  fetchNextPage?: () => Promise<unknown>
  totalItems: number
  isFetching: boolean
  isPlaceholder?: boolean
  swiper: SwiperClass
}

interface ButtonProps {
  icon: IconProp
  ariaLabel: string
  onClick: () => void
  disabled?: boolean
  isLoading?: boolean
}

export const TopNavigation: React.FC<NavigationProps> = ({
  hasNextPage,
  fetchNextPage,
  totalItems,
  isFetching,
  isPlaceholder,
  swiper
}) => {
  // Numeric trigger to force a re-render when Swiper fires a real event
  const [, setTick] = useState(0)
  const forceUpdate = useCallback(() => setTick((t) => t + 1), [])

  const isRewindEnabled = Boolean(swiper.params?.rewind)

  const slidesPerView = swiper.slidesPerViewDynamic()
  const hasMoreSlides = totalItems > slidesPerView

  const isBeginning = swiper.isBeginning || swiper.progress === 0
  const isEnd = swiper.isEnd || swiper.progress === 1

  const handleFetchNextPage = useCallback(async () => {
    if (!fetchNextPage || isFetching) return
    await fetchNextPage()
  }, [fetchNextPage, isFetching])

  // Offset held just before an appended page grows the track. Swiper's own
  // `update()` then re-derives the position from `activeIndex` via
  // `snapGrid[Math.floor(activeIndex / slidesPerGroup)]` — and the last page of
  // a short list sits at the clamped `maxTranslate`, which stops being a snap
  // point once more slides arrive. That lookup resolves to `snapGrid[0]` and
  // throws the track back to the first slide.
  const preGrowthTranslateRef = useRef(0)

  useEffect(() => {
    if (!swiper || swiper.destroyed) return

    // `slidesLengthChange` still sees the pre-merge offset; the jump happens on
    // the following tick.
    const onSlidesLengthChange = (s: SwiperClass) => {
      preGrowthTranslateRef.current = s.translate
    }

    swiper.on('slidesLengthChange', onSlidesLengthChange)

    return () => {
      swiper.off('slidesLengthChange', onSlidesLengthChange)
    }
  }, [swiper])

  useEffect(() => {
    if (!swiper || swiper.destroyed) return

    const timer = setTimeout(() => {
      if (swiper.destroyed) return

      swiper.update()

      // Re-anchor on the offset held before the merge, clamped to the grown
      // track, then snap to the nearest page so `snapIndex` lines back up with
      // `translate`. `runCallbacks: false` keeps this out of the pagination
      // handlers below.
      const anchored = Math.max(
        Math.min(preGrowthTranslateRef.current, swiper.minTranslate()),
        swiper.maxTranslate(),
      )

      if (swiper.translate !== anchored) {
        swiper.setTranslate(anchored)
        swiper.slideToClosest(0, false)
      }

      forceUpdate()
    }, 50)

    return () => clearTimeout(timer)
  }, [totalItems, swiper, forceUpdate])

  useEffect(() => {
    const onSlideChange = (s: SwiperClass) => {
      const activeIndex = s.activeIndex
      const currentSlidesPerView = s.slidesPerViewDynamic()
      const prefetchBuffer = currentSlidesPerView
      const isEnding = activeIndex + currentSlidesPerView + prefetchBuffer >= totalItems

      forceUpdate()

      if (isEnding && !isFetching && hasNextPage) {
        handleFetchNextPage()
      }
    }

    swiper.on('slideChange', onSlideChange)
    swiper.on('progress', forceUpdate)
    swiper.on('reachEnd', forceUpdate)
    swiper.on('reachBeginning', forceUpdate)
    swiper.on('fromEdge', forceUpdate)
    swiper.on('toEdge', forceUpdate)

    return () => {
      swiper.off('slideChange', onSlideChange)
      swiper.off('progress', forceUpdate)
      swiper.off('reachEnd', forceUpdate)
      swiper.off('reachBeginning', forceUpdate)
      swiper.off('fromEdge', forceUpdate)
      swiper.off('toEdge', forceUpdate)
    }
  }, [swiper, totalItems, isFetching, hasNextPage, handleFetchNextPage, forceUpdate])

  const handlePrevPage = () => {
    swiper.slidePrev()
  }

  const handleNextPage = () => {
    if (!isFetching) {
      swiper.slideNext()
    }
  }

  return (
    <div className={classes.container}>
      <NavigationButton
        icon={faChevronLeft}
        ariaLabel="Previous slides"
        onClick={handlePrevPage}
        disabled={isFetching || isBeginning || isPlaceholder}
        isLoading={isPlaceholder || isFetching}
      />
      <NavigationButton
        icon={faChevronRight}
        ariaLabel="Next slides"
        onClick={handleNextPage}
        disabled={
          isFetching ||
          isPlaceholder ||
          (!hasMoreSlides && !hasNextPage) ||
          (isEnd && !hasNextPage && !isRewindEnabled)
        }
        isLoading={isPlaceholder || isFetching}
      />
    </div>
  )
}

const NavigationButton: React.FC<ButtonProps> = (props) => {
  const { icon, ariaLabel, onClick, disabled = false, isLoading } = props
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={classNames({
        [classes.button]: true,
        [classes.button__disabled]: disabled,
        [classes.button__loading]: isLoading,
      })}
    >
      <FontAwesomeIcon icon={icon} />
    </button>
  )
}